import { ApiErrorPresenterService } from './api-error-presenter.service';
import { Injectable, Signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { map } from 'rxjs';

import { DialogCloseReason, DialogService } from '@sui/dialog';
import { LoggerService } from '@services/logger.service';
import {
  DataGridAdapter,
  GridViewPreset,
  GridViewPresetDetails,
  GridViewPresetStore,
} from '@sensika/ag-grid-kit';
import { SaveAgGridSettingsDialogComponent } from 'src/app/ag-grid/components/save-ag-grid-settings-dialog/save-ag-grid-settings-dialog.component';
import {
  ViewId,
  ViewPreset,
} from 'src/app/api/services/view-preset-api.service';
import {
  injectAdminCopyGridViewPresetMutation,
  injectAdminUpdateGridViewPresetMutation,
  injectAdminDeleteViewPresetMutation,
  injectAdminCreateGridViewPresetMutation,
  injectDeleteGridViewPresetMutation,
  injectGridViewPresetsQuery,
  injectSaveGridViewPresetMutation,
  injectSetGridViewPresetFlagsMutation,
  injectAdminSetViewPresetFlagsMutation,
} from 'src/app/api/queries/view-preset.queries';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { NomObjectClass } from 'src/app/core/constants';
import { selectCoreLanguage, selectUser } from 'src/app/core/state';
import { HeaderToolsService } from 'src/app/core/services/header-tools.service';
import { UserAccessLevel } from 'src/app/core/services/user.service';
import { AppState } from 'src/app/core/state/app.state';

/**
 * The web app's side of `DATA_GRID_ADAPTER` (data-grid.adapter.ts): NgRx user
 * and language, the logger, the confirm dialog, the header tools and the
 * Angular Query view-preset persistence. Provided in AppModule by token.
 */
@Injectable({ providedIn: 'root' })
export class DataGridAdapterService implements DataGridAdapter {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly dialogService = inject(DialogService);
  private readonly userId = toSignal(
    this.store.select(selectUser).pipe(map((user) => user?.id ?? null)),
    { requireSync: true },
  );

  readonly logger = inject(LoggerService);
  readonly errorPresenter = inject(ApiErrorPresenterService);
  readonly headerTools = inject(HeaderToolsService);
  readonly language = toSignal(
    this.store
      .select(selectCoreLanguage)
      .pipe(map((language) => ({ rtl: !!language?.rtl }))),
    { requireSync: true },
  );
  // Shared-preset actions mirror the backend's admin levels: Company rows need
  // Client Admin+ of the active company, System rows Platform Admin+. The backend
  // re-checks and answers 403, so these only decide what the menu shows.
  readonly user = toSignal(
    this.store.select(selectUser).pipe(
      map((user) => ({
        listCompanyObjects: !!user?.list_company_objects,
        canManageCompanyPresets:
          (user?.access_level_id ?? 0) >= UserAccessLevel.CLIENT_ADMIN,
        canManageSystemPresets:
          (user?.access_level_id ?? 0) >= UserAccessLevel.PLATFORM_ADMIN,
      })),
    ),
    { requireSync: true },
  );

  confirm(message: string) {
    return this.dialogService
      .openSimpleDialog(ConfirmDialogComponent, {
        data: { message },
        closeOnClick: false,
      })
      .closed.pipe(map((event) => event.reason === DialogCloseReason.Confirm));
  }

  /** Called from the grid's field initialiser, so the Angular Query injectors run in its injection context. */
  viewPresets(viewId: Signal<string | null>): GridViewPresetStore {
    const query = injectGridViewPresetsQuery(viewId as Signal<ViewId | null>);
    const save = injectSaveGridViewPresetMutation();
    const setFlags = injectSetGridViewPresetFlagsMutation();
    const setSharedFlags = injectAdminSetViewPresetFlagsMutation();
    const remove = injectDeleteGridViewPresetMutation();
    const createShared = injectAdminCreateGridViewPresetMutation();
    const promote = injectAdminCopyGridViewPresetMutation();
    const updateShared = injectAdminUpdateGridViewPresetMutation();
    const removeShared = injectAdminDeleteViewPresetMutation();
    // A preset the grid hands over without a context is the caller's own.
    const asRow = (preset: GridViewPreset): ViewPreset => ({
      ...preset,
      context_object_id: preset.context_object_id ?? this.userId(),
      context_object_class_id:
        preset.context_object_class_id ?? NomObjectClass.USER,
    });
    return {
      presets: query.data,
      status: query.status,
      save: (preset, onSuccess) =>
        save.mutate(
          { viewPreset: asRow(preset), saveAsNew: !preset.uuid },
          { onSuccess },
        ),
      setFlags: (preset, flags) =>
        setFlags.mutate({ viewPreset: preset as ViewPreset, flags }),
      setSharedFlags: (preset, flags) =>
        setSharedFlags.mutate({ uuid: preset.uuid as string, flags }),
      delete: (preset, onSuccess) =>
        remove.mutate(
          { uuid: preset.uuid, viewId: viewId() as ViewId },
          { onSuccess },
        ),
      createShared: (preset) => createShared.mutate(preset as ViewPreset),
      promote: (preset, scopeId) =>
        promote.mutate({ viewPreset: preset as ViewPreset, scopeId }),
      updateShared: (preset) => updateShared.mutate(preset as ViewPreset),
      deleteShared: (preset, onSuccess) =>
        removeShared.mutate(preset as ViewPreset, { onSuccess }),
      askDetails: (preset) =>
        this.dialogService
          .openSimpleDialog(SaveAgGridSettingsDialogComponent, {
            data: { viewPreset: preset as ViewPreset },
            closeOnClick: false,
            closeOnEscape: true,
            height: '40%',
          })
          .closed.pipe(
            map(
              (result) =>
                (result.data as GridViewPresetDetails | undefined) ?? null,
            ),
          ),
    };
  }
}
